/**
 * État « code demandé » d'une page apprenant protégée (recette du 02/11/2026) : l'apprenant quitte la page pour lire
 * sa messagerie — sur téléphone, le navigateur décharge souvent l'onglet — puis revient. Il doit retomber sur le champ
 * de saisie, avec l'adresse rappelée et le temps restant, sans redemander de code.
 *
 * Le serveur fait foi (défi en cours renvoyé avec la réponse 403 `code_requis`) ; ce module garde en plus une copie
 * locale datée (`localStorage`, effacée à l'échéance) pour le cas où la réponse du serveur ne le dirait pas. Aucun
 * code n'est stocké, et la clé ne contient pas le jeton du lien.
 */
export type TypeLienPublic = "formulaire" | "positionnement";

export type DefiCode = { envoye_le: string; expire_le: string; essais_restants: number; renvoi_possible_le: string; email_masque?: string };

type Stockage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export const LONGUEUR_CODE = 6;

/** Garde les chiffres d'une saisie ou d'un collage (« 123 456 », « Votre code : 123456. »), 6 au plus. */
export const normaliserCode = (texte: string): string => texte.replace(/\D/g, "").slice(0, LONGUEUR_CODE);

/** Code transmis par le lien de l'e-mail : `#code=123456` (le fragment ne part jamais au serveur). */
export function codeDuFragment(fragment: string): string | null {
  const m = /(?:^#|&)code=(\d{6})(?:&|$)/.exec(fragment);
  return m ? m[1]! : null;
}

/** Empreinte courte et non réversible du jeton (FNV-1a 32 bits) : distingue les liens sans écrire le jeton. */
function empreinte(texte: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < texte.length; i++) {
    h ^= texte.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

export const cleDefi = (type: TypeLienPublic, jeton: string): string => `s4m-code:${type}:${empreinte(jeton)}`;

const valide = (d: DefiCode | null | undefined, maintenant: number): d is DefiCode => Boolean(d && Date.parse(d.expire_le) > maintenant);

export function memoriserDefi(st: Stockage, type: TypeLienPublic, jeton: string, defi: DefiCode): void {
  try {
    st.setItem(cleDefi(type, jeton), JSON.stringify(defi));
  } catch {
    // Stockage refusé (navigation privée, quota) : le serveur suffit.
  }
}

export function oublierDefi(st: Stockage, type: TypeLienPublic, jeton: string): void {
  try {
    st.removeItem(cleDefi(type, jeton));
  } catch {
    // idem
  }
}

export function lireDefi(st: Stockage, type: TypeLienPublic, jeton: string, maintenant = Date.now()): DefiCode | null {
  try {
    const brut = st.getItem(cleDefi(type, jeton));
    if (!brut) return null;
    const d = JSON.parse(brut) as DefiCode;
    if (valide(d, maintenant)) return d;
    st.removeItem(cleDefi(type, jeton));
  } catch {
    // illisible ou indisponible : ignoré
  }
  return null;
}

/**
 * Défi à afficher. `serveur` : `null` = le serveur dit qu'aucun code n'attend (déjà utilisé, échu) → rien ;
 * `undefined` = le serveur ne dit rien → copie locale. Entre deux défis valides, le plus récent l'emporte.
 */
export function defiActif(serveur: DefiCode | null | undefined, local: DefiCode | null, maintenant = Date.now()): DefiCode | null {
  if (serveur === null) return null;
  const candidats = [serveur, local].filter((d): d is DefiCode => valide(d, maintenant));
  return candidats.sort((a, b) => Date.parse(b.envoye_le) - Date.parse(a.envoye_le))[0] ?? null;
}

/** « 14 min 05 s », « 42 s ». */
export function resteLisible(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const min = Math.floor(s / 60);
  const sec = s % 60;
  return min > 0 ? `${min} min ${String(sec).padStart(2, "0")} s` : `${sec} s`;
}
